import type { TokenResponse, WidgetConfig } from '../types';

/**
 * Storage key for persisted token
 */
const TOKEN_STORAGE_KEY = 'easyhelp_widget_token';

/**
 * Buffer before expiry to refresh token (5 minutes)
 */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * Interval for checking if token needs refresh (30 seconds)
 * Short interval is resilient to browser throttling in background tabs
 */
const REFRESH_CHECK_INTERVAL_MS = 30 * 1000;

/**
 * Stored token data
 * SECURITY: Only stores non-sensitive data. Identity comes from JWT claims.
 */
interface StoredToken {
  token: string;
  expiresAt: string;
  conversationId: string | null;
}

/**
 * Token manager for widget authentication
 *
 * Token flow:
 * 1. Widget calls host app endpoint (e.g., /api/easyhelp/token)
 * 2. Host app calls EasyHelp widget-token with API key
 * 3. Host app returns token to widget
 * 4. Widget stores token in localStorage for persistence
 */
export class TokenManager {
  private token: string | null = null;
  private expiresAt: Date | null = null;
  private conversationId: string | null = null;
  private refreshCheckTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private refreshPromise: Promise<void> | null = null;
  private config: WidgetConfig;
  private onTokenRefresh?: (token: string, conversationId: string | null) => void;
  private lastTokenEndpoint: string | null = null;

  constructor(config: WidgetConfig) {
    this.config = config;
    this.loadFromStorage();
  }

  /**
   * Set callback for when token is refreshed
   */
  setRefreshCallback(callback: (token: string, conversationId: string | null) => void): void {
    this.onTokenRefresh = callback;
  }

  /**
   * Handle auth error - clear token state
   */
  handleAuthError(): void {
    this.clearToken();
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    if (!this.token || !this.expiresAt) return null;

    // Check if expired
    if (new Date() >= this.expiresAt) {
      this.clearToken();
      return null;
    }

    return this.token;
  }

  /**
   * Get conversation ID associated with token
   */
  getConversationId(): string | null {
    return this.conversationId;
  }

  /**
   * Check if token needs refresh
   */
  needsRefresh(): boolean {
    if (!this.expiresAt) return true;
    return new Date().getTime() > this.expiresAt.getTime() - REFRESH_BUFFER_MS;
  }

  /**
   * Set token from API response
   */
  setToken(response: TokenResponse): void {
    this.token = response.token;
    this.expiresAt = new Date(response.expires_at);
    // Keep existing conversationId if server returns null (e.g. token refresh)
    if (response.conversation_id) {
      this.conversationId = response.conversation_id;
    }

    // Save to storage
    this.saveToStorage();

    // Schedule refresh
    this.scheduleRefresh();

    // Notify callback
    if (this.onTokenRefresh) {
      this.onTokenRefresh(response.token, response.conversation_id);
    }
  }

  /**
   * Clear token (preserves conversationId for session resumption)
   */
  clearToken(): void {
    this.token = null;
    this.expiresAt = null;
    // Keep conversationId so it can be restored on next token fetch
    this.clearRefreshSchedule();

    try {
      localStorage.removeItem(this.getStorageKey());
      // Persist conversationId separately so it survives token expiry
      if (this.conversationId) {
        localStorage.setItem(this.getConversationStorageKey(), this.conversationId);
      }
    } catch {
      // localStorage might not be available
    }
  }

  /**
   * Fetch token from host application's backend
   * The host app is responsible for calling EasyHelp with the API key
   */
  async fetchToken(hostTokenEndpoint: string): Promise<TokenResponse> {
    this.lastTokenEndpoint = hostTokenEndpoint;

    const response = await fetch(hostTokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: this.config.customerId,
        customer_name: this.config.customerName,
        customer_email: this.config.customerEmail,
        conversation_id: this.conversationId, // Resume existing conversation if any
      }),
    });

    const text = await response.text();

    if (!response.ok) {
      throw new Error(text || `Token endpoint error ${response.status}`);
    }

    if (!text || text.trim() === '') {
      throw new Error('Empty response from token endpoint');
    }

    let data: TokenResponse;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('Invalid JSON response from token endpoint');
    }

    this.setToken(data);
    return data;
  }

  /**
   * Fetch token directly from EasyHelp's public endpoint
   * Used when usePublicToken is enabled in config
   */
  async fetchPublicToken(): Promise<TokenResponse> {
    const publicEndpoint = `${this.config.apiUrl}/widget-public-token`;

    const response = await fetch(publicEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        tenant_id: this.config.tenantId,
        customer_id: this.config.customerId,
        customer_name: this.config.customerName,
        customer_email: this.config.customerEmail,
        locale: 'it',
      }),
    });

    const text = await response.text();

    if (!response.ok) {
      // Try to extract error message from response
      let errorMessage = `Token error ${response.status}`;
      try {
        const errorBody = JSON.parse(text);
        errorMessage = errorBody.error?.message || errorBody.message || errorMessage;
      } catch {
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }

    if (!text || text.trim() === '') {
      throw new Error('Empty response from token endpoint');
    }

    let data: TokenResponse;
    try {
      const parsed = JSON.parse(text);
      // Handle both wrapped and unwrapped response formats
      data = parsed.data || parsed;
    } catch {
      throw new Error('Invalid JSON response from token endpoint');
    }

    this.setToken(data);
    return data;
  }

  /**
   * Get storage key for session
   * SECURITY: Uses generic key - identity comes from JWT, not localStorage key
   */
  private getStorageKey(): string {
    return `${TOKEN_STORAGE_KEY}_session`;
  }

  /**
   * Get storage key for conversation ID (survives token expiry)
   */
  private getConversationStorageKey(): string {
    return `${TOKEN_STORAGE_KEY}_conversation`;
  }

  /**
   * Load token from localStorage
   * SECURITY: Identity validation done by JWT, not stored data
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (!stored) {
        // No token, but try to restore conversationId from separate key
        const savedConvId = localStorage.getItem(this.getConversationStorageKey());
        if (savedConvId) {
          this.conversationId = savedConvId;
        }
        return;
      }

      const data: StoredToken = JSON.parse(stored);
      const expiresAt = new Date(data.expiresAt);

      // Check if expired
      if (new Date() >= expiresAt) {
        // Preserve conversationId before clearing token
        const convId = data.conversationId;
        this.clearToken();
        if (convId) {
          this.conversationId = convId;
        }
        return;
      }

      this.token = data.token;
      this.expiresAt = expiresAt;
      this.conversationId = data.conversationId;

      // Schedule refresh
      this.scheduleRefresh();
    } catch {
      // Invalid stored data
      this.clearToken();
    }
  }

  /**
   * Save token to localStorage
   * SECURITY: Only stores token, expiry, and conversation ID - no PII
   */
  private saveToStorage(): void {
    if (!this.token || !this.expiresAt) return;

    try {
      const data: StoredToken = {
        token: this.token,
        expiresAt: this.expiresAt.toISOString(),
        conversationId: this.conversationId,
      };
      localStorage.setItem(this.getStorageKey(), JSON.stringify(data));
      // Clean up separate conversation key — it's now in the main session
      localStorage.removeItem(this.getConversationStorageKey());
    } catch {
      // localStorage might not be available (private browsing, etc.)
    }
  }

  /**
   * Schedule reliable token refresh using interval check + visibility events.
   * Unlike setTimeout (which browsers throttle in background tabs),
   * this approach checks every 30s and on tab visibility change.
   */
  private scheduleRefresh(): void {
    this.clearRefreshSchedule();

    if (!this.expiresAt) return;

    this.refreshCheckTimer = setInterval(() => {
      this.checkAndRefresh();
    }, REFRESH_CHECK_INTERVAL_MS);

    this.visibilityHandler = () => {
      if (!document.hidden) {
        this.checkAndRefresh();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Clear all refresh scheduling (timer + event listener)
   */
  private clearRefreshSchedule(): void {
    if (this.refreshCheckTimer) {
      clearInterval(this.refreshCheckTimer);
      this.refreshCheckTimer = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  /**
   * Check if token needs refresh and perform it.
   * Serializes concurrent calls through a single promise.
   */
  private async checkAndRefresh(): Promise<void> {
    if (!this.needsRefresh()) return;
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      try {
        if (this.config.usePublicToken) {
          await this.fetchPublicToken();
        } else if (this.lastTokenEndpoint) {
          await this.fetchToken(this.lastTokenEndpoint);
        }
      } catch (err) {
        console.error('[EasyHelp] Token auto-refresh failed:', err);
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Ensure token is valid. If it needs refresh, refresh and return new token.
   * Used by WidgetApiClient to proactively refresh before API calls.
   */
  async ensureValidToken(): Promise<string | null> {
    if (!this.needsRefresh() && this.token) {
      return this.token;
    }
    await this.checkAndRefresh();
    return this.token;
  }

  /**
   * Cleanup
   */
  destroy(): void {
    this.clearRefreshSchedule();
  }
}
